/**
 * ComparatorDetailView — R5 (side-nav comparator detail).
 *
 * Renders the selected comparator's three sections (Demographics, Clinical
 * History, Consent) as anchored summary cards in the second column. Each card
 * has an Edit button that opens the corresponding focused `<ComparatorModal>`
 * (fieldGroups = 'demographics' | 'clinical' | 'consent'). The view replaces
 * the patient sections while a comparator is selected; the side-nav's Patient
 * group is the way back to the patient view.
 *
 * Presentation-only: it reads a single canonical `Comparator` and emits edit /
 * remove intents. No Redux, no autosave.
 */

import { TrashBin } from 'flowbite-react-icons/outline'

import { FormCard, StatusPill } from '@/components/order/FormCard'
import { PrimaryGuardianBadge } from '@/components/order/FamilyMemberDisplayCard'
import { formatDobForDisplay } from '@/components/order/familyMemberDisplayHelpers'
import type { ComparatorParentConsentConfigSlice } from '@/components/order/comparatorParentConsentConfig'
import {
  comparatorConsentAnyApplicable,
  resolveConsentInteractionGates,
} from '@/components/order/comparatorParentConsentConfig'
import type { Comparator } from '@/ordering/canonicalOrder'
import {
  groupComparatorHpoTerms,
  parseComparatorHpoTerms,
} from '@/ordering/comparatorHpoTerms'
import { useResolvedHpoTermNames } from '@/components/order/useResolvedHpoTermNames'
import { useMemo } from 'react'
import { formatComparatorRelationshipLabel } from '@/ordering/comparatorRelationshipOptions'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import { getPhenotypicTraitLabel, PhenotypicTrait, Sex } from '@/types/RequisitionFormTypes'
import {
  formatSexForDisplay,
  getSampleTypeLabel,
  mapCollectionStringToSampleTypeStrict,
} from '@/utils/orderFormUtils'

export interface ComparatorDetailAnchors {
  demographics: string
  clinical: string
  consent: string
}

interface ComparatorDetailViewProps {
  comparator: Comparator
  anchors: ComparatorDetailAnchors
  consentConfigs: ComparatorParentConsentConfigSlice
  /**
   * Section edit intents. Omit (or pass `undefined`) to render that section
   * read-only: no Edit button and no clickable surface (e.g. a submitted order
   * the user can't edit). Mirrors `onRemove`.
   */
  onEditDemographics?: () => void
  onEditClinical?: () => void
  onEditConsent?: () => void
  /**
   * Remove this comparator from the order. Omit to hide the action (e.g. once
   * the sample is accessioned, comparators can be added but not removed).
   */
  onRemove?: () => void
  /**
   * When true, render the Remove action visibly disabled instead of active.
   * Used for the fail-closed sample-collection gate: a comparator whose lab
   * sample has been collected (or whose collection status is still loading /
   * errored) cannot be removed, but the affordance stays visible with an
   * explanation rather than disappearing. Ignored when `onRemove` is omitted.
   */
  removeDisabled?: boolean
  /**
   * Explanation shown as the disabled Remove button's tooltip / accessible
   * description (e.g. "This comparator's sample has been collected
   * and can no longer be removed."). Only used when `removeDisabled` is true.
   */
  removeDisabledReason?: string
  /**
   * When true, MRN counts toward this comparator's completeness (drives the
   * required accent bar). The post-submit page (OrderInProgress) requires MRN
   * for a selected parent; the draft flow (OrderStart) leaves it optional.
   */
  mrnRequired?: boolean
  /**
   * When true, the proband opted into research contact, so the primary-guardian
   * comparator (this row, if flagged) must supply a contact channel to be
   * complete — regardless of relationship or testing status.
   */
  probandResearchContactConsent?: boolean
  /**
   * Optional display label for the header/title. When the same relationship
   * appears multiple times on an order (e.g. two siblings), the side-nav
   * numbers them ("Sibling 1", "Sibling 2"); pass that numbered label here so
   * the detail view header matches the nav. Falls back to the plain
   * relationship label when omitted.
   */
  titleLabel?: string
  /**
   * When true, the view suppresses its own relationship/name `<h2>` header and
   * the Remove action, rendering only the three anchored section cards. Used by
   * the stacked order layout, where a per-person group wrapper on the page owns
   * a single header + Remove control above the cards. Defaults to false, which
   * preserves the standalone (header-owning) behavior.
   */
  hideOwnHeader?: boolean
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium uppercase tracking-wide text-text-body-subtle dark:text-gray-500">
        {label}
      </span>
      <span className="text-sm text-text-body dark:text-gray-400">{value || '—'}</span>
    </div>
  )
}

/** "a", "a and b", "a, b and c". */
function joinLabels(labels: string[]): string {
  return labels.length <= 1 ? (labels[0] ?? '') : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`
}

function SectionStatusPill({ missing }: { missing: string[] }) {
  return missing.length > 0 ? (
    <StatusPill chip text={`Add ${joinLabels(missing)} to complete this section`} />
  ) : (
    <StatusPill variant="success" text="Complete" />
  )
}

function incidentalLabel(value: boolean | null | undefined): string {
  if (value === true) return 'Opt in'
  if (value === false) return 'Opt out'
  return 'Not provided'
}

function yesNo(value: boolean | null | undefined): string {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return 'Not provided'
}

export function ComparatorDetailView({
  comparator,
  anchors,
  consentConfigs,
  onEditDemographics,
  onEditClinical,
  onEditConsent,
  onRemove,
  removeDisabled = false,
  removeDisabledReason,
  mrnRequired = false,
  probandResearchContactConsent = false,
  titleLabel,
  hideOwnHeader = false,
}: ComparatorDetailViewProps) {
  const demo = comparator.demographic ?? {}
  const relationshipLabel =
    titleLabel?.trim() || formatComparatorRelationshipLabel(comparator.relationship) || 'Comparator'
  const name = [demo.firstName, demo.lastName]
    .filter((p) => p && String(p).trim())
    .join(' ')
    .trim()
  const sampleType = mapCollectionStringToSampleTypeStrict(comparator.specimen?.collectionType ?? null)
  const sampleTypeLabel = sampleType ? getSampleTypeLabel(sampleType) : '—'
  const sexLabel = formatSexForDisplay((demo.sex?.trim() as Sex | undefined) ?? undefined)
  const traitLabel =
    comparator.phenotypicTrait && comparator.phenotypicTrait !== PhenotypicTrait.Unknown
      ? getPhenotypicTraitLabel(comparator.phenotypicTrait as PhenotypicTrait)
      : getPhenotypicTraitLabel(PhenotypicTrait.Unknown)
  const clinicalNote = comparator.clinicalNote?.trim() ?? ''
  const hpoTerms = useResolvedHpoTermNames(
    useMemo(() => parseComparatorHpoTerms(comparator.hpoTerms), [comparator.hpoTerms])
  )
  const phenotypeGroups = groupComparatorHpoTerms(hpoTerms)
  const consent = comparator.secondaryConsent ?? {}
  const consentApplicable = comparatorConsentAnyApplicable(consentConfigs)
  // Per-section completeness so only the section actually missing data shows the
  // required accent — the overall row-completeness predicate flagged all three
  // cards at once. Each helper mirrors `isCanonicalComparatorComplete` scoped to
  // the fields that section renders.
  const demographicsMissing = FormCompletionChecker.comparatorDemographicsMissing(comparator, {
    mrnRequired,
    probandResearchContactConsent,
  })
  const clinicalMissing = FormCompletionChecker.comparatorClinicalComplete(comparator) ? [] : ['affected status']
  const consentGates = resolveConsentInteractionGates(consentConfigs)
  const consentMissing = FormCompletionChecker.comparatorConsentMissing(comparator, {
    incidentalApplicable: consentGates.incidentalRequireInteraction,
    nyRetentionRequireInteraction: consentGates.nyRetentionRequireInteraction,
    researchContactRequireInteraction: consentGates.researchContactRequireInteraction,
  })

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      {!hideOwnHeader && (
        <>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold tracking-tight text-text-heading dark:text-white">
              {relationshipLabel}
              {name ? <span className="text-text-body-subtle dark:text-gray-500"> · {name}</span> : null}
            </h2>
            {onRemove ? (
              removeDisabled ? (
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  aria-label={`Remove ${relationshipLabel}${name ? ` (${name})` : ''} (unavailable)`}
                  title={removeDisabledReason}
                  className="ml-auto inline-flex cursor-not-allowed items-center gap-1.5 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-text-disabled dark:text-gray-600 shadow-xs"
                >
                  <TrashBin className="h-4 w-4" aria-hidden />
                  Remove Comparator
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onRemove}
                  aria-label={`Remove ${relationshipLabel}${name ? ` (${name})` : ''}`}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border-error-subtle dark:border-error-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-text-fg-error dark:text-error-100 shadow-xs transition-colors hover:bg-error-50 dark:hover:bg-gray-800"
                >
                  <TrashBin className="h-4 w-4" aria-hidden />
                  Remove Comparator
                </button>
              )
            ) : null}
          </div>

          {onRemove && removeDisabled && removeDisabledReason ? (
            <p className="text-xs font-medium text-text-body-subtle dark:text-gray-500">{removeDisabledReason}</p>
          ) : null}
        </>
      )}

      <section id={anchors.demographics} className="scroll-mt-28">
        <FormCard
          title="Demographics"
          onEdit={onEditDemographics}
          clickableSurface
          appearance="status"
          required
          completed={demographicsMissing.length === 0}
          badge={<SectionStatusPill missing={demographicsMissing} />}
        >
          <div className="flex flex-col gap-4">
            {comparator.isPrimaryGuardian === true ? (
              <div className="flex flex-wrap gap-2">
                <PrimaryGuardianBadge>Primary guardian</PrimaryGuardianBadge>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FieldRow label="Name" value={name} />
              <FieldRow label="Sex assigned at birth" value={sexLabel} />
              <FieldRow label="Date of birth" value={formatDobForDisplay(demo.dateOfBirth)} />
              <FieldRow label="MRN" value={demo.mrn?.trim() ?? ''} />
              <FieldRow label="Email" value={demo.email?.trim() ?? ''} />
              <FieldRow label="Phone" value={demo.phone?.trim() ?? ''} />
              <FieldRow label="Sample type" value={sampleTypeLabel} />
            </div>
          </div>
        </FormCard>
      </section>

      <section id={anchors.clinical} className="scroll-mt-28">
        <FormCard
          title="Clinical History"
          onEdit={onEditClinical}
          clickableSurface
          appearance="status"
          required
          completed={clinicalMissing.length === 0}
          badge={<SectionStatusPill missing={clinicalMissing} />}
        >
          <div className="flex flex-col gap-4">
            <FieldRow label="Affected Status" value={traitLabel} />
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium uppercase tracking-wide text-text-body-subtle dark:text-gray-500">
                Clinical summary
              </span>
              <span className="whitespace-pre-wrap text-sm text-text-body dark:text-gray-400">
                {clinicalNote || 'None entered'}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-3">
              {phenotypeGroups.map((group) => (
                <div key={group.title} className="flex flex-col gap-2 sm:gap-3">
                  <h3 className="text-xs font-medium leading-5 text-text-heading dark:text-white sm:text-sm">
                    {group.title}
                  </h3>
                  <div className="flex flex-col gap-1 sm:gap-2">
                    {group.isEmpty ? (
                      <span className="text-sm leading-5 text-text-body-subtle dark:text-gray-500">
                        None entered
                      </span>
                    ) : (
                      group.items.map((item) => (
                        <span
                          key={item}
                          className="text-sm leading-5 text-text-body dark:text-gray-400"
                        >
                          {item}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FormCard>
      </section>

      <section id={anchors.consent} className="scroll-mt-28">
        <FormCard
          title="Additional Options"
          onEdit={onEditConsent}
          clickableSurface
          appearance="status"
          required
          completed={consentMissing.length === 0}
          badge={<SectionStatusPill missing={consentMissing} />}
        >
          {consentApplicable ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {consentConfigs.incidental?.isApplicable ? (
                <FieldRow
                  label="Incidental findings"
                  value={incidentalLabel(consent.includeIncidentalFindings)}
                />
              ) : null}
              {consentConfigs.research?.isApplicable ? (
                <FieldRow label="Research contact" value={yesNo(consent.researchContactConsent)} />
              ) : null}
              {consentConfigs.ny?.isApplicable ? (
                <FieldRow
                  label="NY sample retention"
                  value={yesNo(consent.newYorkSampleRetentionOptIn)}
                />
              ) : null}
            </div>
          ) : (
            <span className="text-sm text-text-body-subtle dark:text-gray-500">
              No additional options are available for this comparator.
            </span>
          )}
        </FormCard>
      </section>
    </div>
  )
}
